/**
 * Sidebar navigation — the shape of the menu, separate from the chrome that
 * draws it.
 *
 * The menu was a flat list of twenty entries ordered by a hand-kept `order`
 * number. Twenty items is past the point where a flat list is scannable: you
 * read it rather than glance at it, and "Log Master" sitting between "Errors"
 * and "Partners" is an accident of numbering, not a statement about what
 * belongs with what. So the same entries are now arranged in three levels:
 *
 *     Group        a heading — Administration, Org, …
 *       Collection a screen you can navigate to
 *         Item     a tab within that screen, deep-linked with `?tab=`
 *
 * What is NOT hardcoded here is which entries a user sees. That still comes
 * from the permissions payload exactly as before: this file declares the shape,
 * `buildNav` filters it down to what the signed-in user may open, and a group
 * or collection with nothing left under it does not render.
 */
import { JOB_NAMES } from '../../constants/jobNames';
import { hasAnyPermissionForJob } from '../../constants/permissions';

/** A tab inside a collection's screen. Level 3. */
export interface NavItem {
  id: string;
  label: string;
  /** Route including the `?tab=` the group screen reads. */
  route: string;
  /** Governing jobs, OR'd. Mirrors the tab's own gate in the group screen. */
  jobs: string[];
}

/** A screen. Level 2. Carries children only when its screen is tabbed. */
export interface NavCollection {
  id: string;
  label: string;
  icon: string;
  route: string;
  jobs: string[];
  children: NavItem[];
}

/** A heading. Level 1. Never navigates — it only opens and closes. */
export interface NavGroup {
  id: string;
  label: string;
  collections: NavCollection[];
}

/**
 * The taxonomy.
 *
 * Order here is the DEFAULT order, which the user's saved arrangement overrides.
 * Groups read top to bottom in the order declared; so do the collections inside
 * them. There is no `order` number to keep in sync any more — position in the
 * array is the position on screen.
 */

/** Shorthand so the table below reads as a table. */
const item = (id: string, label: string, route: string, jobs: string[]): NavItem => ({
  id,
  label,
  route,
  jobs,
});

export const NAV_TAXONOMY: NavGroup[] = [
  {
    // Reading documents and extracting their data: OCR through a registered
    // document-analysis model, then an LLM. Gated on AI Models for now, because
    // that is the permission the analyze-document endpoint checks.
    id: 'document-intelligence',
    label: 'Document Intelligence',
    collections: [
      {
        id: 'di-upload',
        label: 'Upload Document',
        icon: 'upload',
        route: '/document-intelligence/upload',
        jobs: [JOB_NAMES.AI_MODEL_MASTER],
        children: [],
      },
      {
        id: 'di-saved',
        label: 'Saved Documents',
        icon: 'folder',
        route: '/document-intelligence/saved',
        jobs: [JOB_NAMES.AI_MODEL_MASTER],
        children: [],
      },
      {
        // The document categories and the fields to extract from each — the
        // extraction schema the other two pages run on.
        id: 'di-master',
        label: 'Documents Master',
        icon: 'list',
        route: '/document-intelligence/master',
        jobs: [JOB_NAMES.AI_MODEL_MASTER],
        children: [],
      },
    ],
  },
  {
    id: 'ai',
    label: 'AI',
    collections: [
      {
        id: 'aimodels',
        label: 'AI Models',
        icon: 'pi pi-server',
        route: '/ai-models',
        jobs: [JOB_NAMES.AI_MODEL_MASTER],
        children: [],
      },
      {
        id: 'evals',
        label: 'Evals',
        icon: 'pi pi-check-circle',
        route: '/eval-cases',
        jobs: [JOB_NAMES.EVAL_CASE, JOB_NAMES.EVALS, JOB_NAMES.EVAL_RUNS],
        children: [
          item('evals.cases', 'Eval Cases', '/eval-cases', [JOB_NAMES.EVAL_CASE]),
          item('evals.suites', 'Eval Suites', '/evals', [JOB_NAMES.EVALS]),
          item('evals.runs', 'Eval Runs', '/eval-runs', [JOB_NAMES.EVAL_RUNS]),
        ],
      },
      {
        id: 'knowledge-bases',
        label: 'Knowledge Bases',
        icon: 'pi pi-database',
        route: '/knowledge-bases',
        jobs: [JOB_NAMES.KNOWLEDGE_BASE],
        children: [],
      },
      {
        id: 'prompts',
        label: 'Prompts',
        icon: 'pi pi-comments',
        route: '/prompts',
        jobs: [JOB_NAMES.PROMPT_MASTER, JOB_NAMES.PROMPT_EXECUTIONS],
        children: [
          item('prompts.prompt-master', 'Prompt Master', '/prompts?tab=prompt-master', [JOB_NAMES.PROMPT_MASTER]),
          item('prompts.prompt-executions', 'Executions', '/prompts?tab=prompt-executions', [JOB_NAMES.PROMPT_EXECUTIONS]),
        ],
      },
      {
        id: 'rags',
        label: 'RAGs',
        icon: 'pi pi-book',
        route: '/rags',
        jobs: [JOB_NAMES.RAG],
        children: [],
      },
      {
        id: 'agents',
        label: 'Agents',
        icon: 'pi pi-android',
        route: '/agents',
        jobs: [JOB_NAMES.AGENT],
        children: [],
      },
      {
        id: 'chat',
        label: 'Chat',
        icon: 'pi pi-comment',
        route: '/chat',
        jobs: [JOB_NAMES.CONVERSATIONS],
        children: [],
      },
      {
        id: 'conversations',
        label: 'Conversations',
        icon: 'pi pi-comments',
        route: '/conversations',
        jobs: [JOB_NAMES.CONVERSATIONS],
        children: [],
      },
      {
        id: 'pipelines',
        label: 'Pipelines',
        icon: 'pi pi-sitemap',
        route: '/pipelines',
        jobs: [JOB_NAMES.PIPELINE, JOB_NAMES.PIPELINE_RUNS],
        children: [
          item('pipelines.pipelines', 'Pipelines', '/pipelines', [JOB_NAMES.PIPELINE]),
          item('pipelines.runs', 'Pipeline Runs', '/pipeline-runs', [JOB_NAMES.PIPELINE_RUNS]),
        ],
      },
      {
        id: 'ai-observability',
        label: 'Observability',
        icon: 'pi pi-chart-line',
        route: '/ai-observability',
        // Same job as the AI monitor — see the note in config/route-permissions.ts
        // for why this reuses a permission instead of introducing one.
        jobs: [JOB_NAMES.PROMPT_EXECUTIONS],
        children: [
          item('ai-observability.dashboard', 'Overview', '/ai-observability', [JOB_NAMES.PROMPT_EXECUTIONS]),
          item('ai-observability.master', 'Observability Master', '/ai-observability/master', [JOB_NAMES.PROMPT_EXECUTIONS]),
        ],
      },
      {
        id: 'functions',
        label: 'Functions',
        icon: 'pi pi-bolt',
        route: '/functions',
        jobs: [JOB_NAMES.FUNCTION_MASTER],
        children: [],
      },
      {
        // What people said about what the models produced. The same collections
        // and the same review queue as the Feedback module under Software Engg —
        // filtered to the component types that describe model output, because
        // the person who answers for a wrong answer is not the person who
        // answers for a broken screen.
        id: 'ai-feedback',
        label: 'AI Feedback',
        icon: 'message-dots',
        route: '/ai-feedback',
        jobs: [JOB_NAMES.FEEDBACK_SUBMISSION, JOB_NAMES.FEEDBACK_REVIEW, JOB_NAMES.FEEDBACK_MASTER],
        children: [
          item('ai-feedback.submissions', 'AI Submissions', '/ai-feedback?tab=submissions', [JOB_NAMES.FEEDBACK_SUBMISSION]),
          item('ai-feedback.reviews', 'AI Reviews', '/ai-feedback?tab=reviews', [JOB_NAMES.FEEDBACK_REVIEW]),
          item('ai-feedback.definitions', 'AI Feedback Forms', '/ai-feedback?tab=definitions', [JOB_NAMES.FEEDBACK_MASTER]),
        ],
      },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    collections: [
      {
        id: 'partners',
        label: 'Partners',
        icon: 'handshake',
        route: '/partners',
        jobs: [JOB_NAMES.PARTNERS, JOB_NAMES.PARTNER_CONNECTIONS, JOB_NAMES.CONNECTION_TYPES],
        children: [
          item('partners.partners', 'Partners', '/partners?tab=partners', [JOB_NAMES.PARTNERS]),
          item('partners.connections', 'Connections', '/partners?tab=connections', [JOB_NAMES.PARTNER_CONNECTIONS]),
          item('partners.types', 'Connection Types', '/partners?tab=types', [JOB_NAMES.CONNECTION_TYPES]),
        ],
      },
      {
        id: 'apps',
        label: 'Projects',
        // Supplied artwork (lightbulb + gears). Scoped to this entry: the other
        // nine pi-th-large uses keep the Lucide grid glyph.
        icon: 'asset-projects',
        route: '/projects',
        jobs: [JOB_NAMES.APPS],
        children: [],
      },
      {
        // Beside Projects on purpose: project settings and application
        // configuration are now different things, and the split is only legible
        // if the menu shows it.
        id: 'appconfig',
        label: 'App Configuration',
        icon: 'pi pi-sliders-h',
        route: '/app-config',
        jobs: [JOB_NAMES.APP_CONFIG],
        children: [],
      },
      {
        id: 'accesscontrol',
        label: 'RBAC',
        icon: 'pi pi-shield',
        route: '/access-control',
        jobs: [
          JOB_NAMES.ROLES,
          JOB_NAMES.PERMISSIONS,
          JOB_NAMES.PERMISSION_MAPPINGS,
          JOB_NAMES.USER_ROLE_PERMISSIONS,
        ],
        children: [],
      },
      {
        id: 'usermanagement',
        label: 'Users',
        // Scoped to this entry: the other pi-users uses keep the Lucide `users` glyph.
        icon: 'users-round',
        route: '/user-management',
        jobs: [JOB_NAMES.USER_MANAGEMENT],
        children: [],
      },
      {
        id: 'personnel',
        label: 'Personnel',
        icon: 'pi pi-id-card',
        route: '/personnel',
        jobs: [JOB_NAMES.PERSONNEL],
        children: [],
      },
      {
        id: 'designationmaster',
        label: 'Designations',
        icon: 'pi pi-briefcase',
        route: '/designation-master',
        jobs: [JOB_NAMES.DESIGNATION_MASTER],
        children: [],
      },
    ],
  },
  {
    id: 'org',
    label: 'Org',
    collections: [
      {
        id: 'units',
        label: 'Units',
        icon: 'pi pi-building',
        route: '/units',
        jobs: [JOB_NAMES.UNITS, JOB_NAMES.UNIT_TYPE, JOB_NAMES.UNIT_VILLAGES],
        children: [
          item('units.units', 'Unit Master', '/units?tab=units', [JOB_NAMES.UNITS]),
          item('units.unit-types', 'Unit Types', '/units?tab=unit-types', [JOB_NAMES.UNIT_TYPE]),
          item('units.unit-villages', 'Unit Villages', '/units?tab=unit-villages', [JOB_NAMES.UNIT_VILLAGES]),
        ],
      },
      {
        id: 'organization',
        label: 'Organization',
        icon: 'pi pi-sitemap',
        route: '/organization',
        jobs: [JOB_NAMES.MINISTRIES, JOB_NAMES.DEPARTMENTS, JOB_NAMES.DISTRICTS],
        children: [
          item('organization.ministries', 'Ministries', '/organization?tab=ministries', [JOB_NAMES.MINISTRIES]),
          item('organization.departments', 'Departments', '/organization?tab=departments', [JOB_NAMES.DEPARTMENTS]),
          item('organization.districts', 'Districts', '/organization?tab=districts', [JOB_NAMES.DISTRICTS]),
          item('organization.org-structure', 'Org Structure', '/organization?tab=org-structure', [JOB_NAMES.UNITS]),
        ],
      },
    ],
  },
  {
    id: 'softwareengg',
    label: 'Software Engg',
    collections: [
      {
        id: 'errormaster',
        label: 'Error Master',
        icon: 'pi pi-exclamation-triangle',
        route: '/error-master',
        jobs: [JOB_NAMES.ERROR_MASTER],
        children: [],
      },
      {
        id: 'logmaster',
        label: 'Log Master',
        icon: 'pi pi-file-edit',
        route: '/log-master',
        jobs: [JOB_NAMES.LOG_MASTER],
        children: [],
      },
      {
        id: 'logs',
        label: 'Transactional Dashboard',
        icon: 'repeat-2',
        route: '/logs',
        jobs: [JOB_NAMES.ERROR_LOGS, JOB_NAMES.LOG_TRANSACTION, JOB_NAMES.BACKGROUND_JOB, JOB_NAMES.NOTIFICATION_RUNS],
        children: [
          item('logs.error-logs', 'Error Dashboard', '/logs?tab=error-logs', [JOB_NAMES.ERROR_LOGS]),
          item('logs.transaction', 'Audit Log Dashboard', '/logs?tab=transaction', [JOB_NAMES.LOG_TRANSACTION]),
          // The record of what the notification engine fired — moved here from
          // the Notification Engine group.
          item('logs.notifications', 'Notifications', '/logs?tab=notifications', [JOB_NAMES.NOTIFICATION_RUNS]),
          item('logs.background-job-dashboard', 'Background Job Dashboard', '/logs?tab=background-job-dashboard', [JOB_NAMES.BACKGROUND_JOB]),
        ],
      },
      {
        // The configuration-driven engine. Kept as its own group rather than as
        // children of Notifications: those tabs belong to the live system, and
        // folding two different models under one label is how an administrator
        // ends up editing the wrong one.
        id: 'notification-engine',
        label: 'Notification Engine',
        icon: 'bell-ring',
        route: '/notification-engine',
        jobs: [
          JOB_NAMES.NOTIFICATIONS,
          JOB_NAMES.NOTIFICATION_TYPE, JOB_NAMES.NOTIFICATION_MASTER,
          JOB_NAMES.RECIPIENT_MASTER, JOB_NAMES.RECIPIENT_LIST,
          JOB_NAMES.ACTION_MASTER, JOB_NAMES.SCHEDULE_MASTER,
        ],
        // Each child opens a tab of the one screen, as the live Notifications
        // group does. The standalone routes still work — a create or edit form
        // navigates to them — but the sidebar no longer sends you to a page
        // that has to be left again to see anything next to it.
        children: [
          // No jobs on these two: a person's own inbox and preferences are not
          // configuration permissions, and gating them would hide the screens
          // from everyone who only receives notifications.
          item('engine.inbox', 'Inbox', '/notification-engine?tab=inbox', []),
          item('engine.preferences', 'Settings', '/notification-engine?tab=preferences', []),
          // Types/Master and Recipients/Lists now share a tab each, with a sub-tab
          // inside. The sidebar still lists all four, because they are four
          // different screens to a person looking for one — the `&view=` carries
          // the inner choice so a menu click lands on the right list, not just
          // the right tab.
          item('engine.masters', 'Notification Master', '/notification-engine?tab=notifications&view=master', [JOB_NAMES.NOTIFICATION_MASTER]),
          item('engine.types', 'Notification Types', '/notification-engine?tab=notifications&view=types', [JOB_NAMES.NOTIFICATION_TYPE]),
          item('engine.recipients', 'Recipient Master', '/notification-engine?tab=recipients&view=people', [JOB_NAMES.RECIPIENT_MASTER]),
          item('engine.lists', 'Recipient Lists', '/notification-engine?tab=recipients&view=lists', [JOB_NAMES.RECIPIENT_LIST]),
          item('engine.actions', 'Actions', '/notification-engine?tab=actions', [JOB_NAMES.ACTION_MASTER]),
          item('engine.schedules', 'Schedules', '/notification-engine?tab=schedules', [JOB_NAMES.SCHEDULE_MASTER]),
        ],
      },
      {
        id: 'feedback',
        label: 'Feedbacks',
        icon: 'message-dots',
        route: '/feedback',
        // One entry per tab in feedback-group.tsx, gated on the SAME job the tab
        // itself is gated on — the three parts of the loop are three different
        // permissions, so an SME with only FEEDBACK_REVIEW reaches their queue
        // without being able to see anyone's definitions.
        jobs: [JOB_NAMES.FEEDBACK_MASTER, JOB_NAMES.FEEDBACK_SUBMISSION, JOB_NAMES.FEEDBACK_REVIEW],
        children: [
          item('feedback.definitions', 'Feedback Master', '/feedback?tab=definitions', [JOB_NAMES.FEEDBACK_MASTER]),
          item('feedback.mine', 'My Feedback', '/feedback?tab=mine', [JOB_NAMES.FEEDBACK_SUBMISSION]),
          item('feedback.submissions', 'Feedback Submission', '/feedback?tab=submissions', [JOB_NAMES.FEEDBACK_SUBMISSION]),
          item('feedback.reviews', 'Feedback Review', '/feedback?tab=reviews', [JOB_NAMES.FEEDBACK_REVIEW]),
        ],
      },
      {
        id: 'valuesets',
        label: 'Value Sets',
        icon: 'list-plus',
        route: '/value-sets',
        jobs: [JOB_NAMES.VALUE_SETS],
        children: [],
      },
      {
        id: 'backgroundjobs',
        label: 'Background Jobs',
        icon: 'cpu',
        route: '/background-jobs',
        jobs: [JOB_NAMES.BACKGROUND_JOB],
        children: [
          item('backgroundjobs.jobs', 'Background Job Master', '/background-jobs?tab=jobs', [JOB_NAMES.BACKGROUND_JOB]),
          item('backgroundjobs.schedules', 'Background Scheduler', '/background-jobs?tab=schedules', [JOB_NAMES.BACKGROUND_JOB]),
          item('backgroundjobs.settings', 'Background Settings', '/background-jobs?tab=settings', [JOB_NAMES.BACKGROUND_JOB]),
        ],
      },
    ],
  },
];

/** The one entry that is not part of any group — it sits above them all. */
export const DASHBOARD: NavCollection = {
  id: 'dashboard',
  label: 'Dashboard',
  icon: 'pi pi-home',
  route: '/dashboard',
  jobs: [],
  children: [],
};

/**
 * Backend job (normalised) -> the collection it belongs to.
 *
 * Unchanged in meaning from the flat menu: several jobs can fold into one
 * collection, which is why "unit-types" lights up "Units" rather than an entry
 * of its own.
 */
export const JOB_TO_COLLECTION: Record<string, string | string[]> = {
  units: 'units', unittype: 'units', unitvillages: 'units',
  ministries: 'organization', departments: 'organization', district: 'organization',
  prompttable: 'prompts', promptexecutions: 'prompts',
  aimodelmaster: 'aimodels', evalcase: 'evals', evals: 'evals', evalrun: 'evals',
  knowledgebase: 'knowledge-bases',
  pipeline: 'pipelines', pipelinerun: 'pipelines',
  notifications: 'notification-engine', notificationmaster: 'notification-engine',
  notificationtype: 'notification-engine', recipientmaster: 'notification-engine',
  recipientlist: 'notification-engine', actionmaster: 'notification-engine',
  schedulemaster: 'notification-engine',
  // Notification runs moved to the Transactional Dashboard (logs) group.
  notificationruns: 'logs',
  // All three feedback jobs light up the one Feedback collection, so a user who
  // holds only FEEDBACK_REVIEW still gets the menu entry that leads to their queue.
  // The three feedback jobs light TWO screens: the Feedback module and the AI
  // Feedback screen. They are one permission over one collection shown in two
  // windows, so a job that opened only one of them would leave the other
  // permanently hidden for everyone whose menu comes from the jobs payload.
  feedbackmaster: ['feedback', 'ai-feedback'],
  feedbacksubmission: ['feedback', 'ai-feedback'],
  feedbackreview: ['feedback', 'ai-feedback'],
  errorlogs: 'logs', logtransaction: 'logs',
  partner: 'partners', partnerconnection: 'partners', connectiontype: 'partners',
  accesscontrol: 'accesscontrol', usermanagement: 'usermanagement', personnel: 'personnel',
  designationmaster: 'designationmaster', apps: 'apps', valuesets: 'valuesets',
  appconfig: 'appconfig',
  errormaster: 'errormaster', logmaster: 'logmaster',
  backgroundjob: 'backgroundjobs', backgroundjobrun: 'backgroundjobs',
};

export const normalizeJobName = (name: string): string =>
  name.toLowerCase().replace(/[-_\s]/g, '');

/**
 * Cut the taxonomy down to what this user may open.
 *
 * `visibleIds` is the permission-driven set of collection ids, resolved by the
 * caller exactly as the flat menu resolved it. Children are gated separately on
 * their own jobs, so a user who may see Units but not Unit Types gets the
 * collection with that one tab missing — the same rule `GroupScreen` applies to
 * the tab strip itself, so the two never disagree.
 *
 * A group whose collections all vanish does not render: no empty headings.
 */
export const buildNav = (visibleIds: Set<string>, permissionKeys: string[]): NavGroup[] => {
  const keys = new Set<string>(permissionKeys);
  const allowed = (jobs: string[]) =>
    jobs.length === 0 || jobs.some((j) => hasAnyPermissionForJob(j, keys));

  return NAV_TAXONOMY.map((g) => ({
    ...g,
    collections: g.collections
      .filter((c) => visibleIds.has(c.id))
      .map((c) => ({ ...c, children: c.children.filter((i) => allowed(i.jobs)) })),
  })).filter((g) => g.collections.length > 0);
};

/** Every collection id the taxonomy knows, for the permission resolver. */
export const ALL_COLLECTION_IDS: string[] = NAV_TAXONOMY.flatMap((g) =>
  g.collections.map((c) => c.id)
);

/** Collection id -> its governing jobs, for the fallback visibility path. */
export const COLLECTION_JOBS: Record<string, string[]> = Object.fromEntries(
  NAV_TAXONOMY.flatMap((g) => g.collections.map((c) => [c.id, c.jobs] as const))
);
